import { useState } from "react";

import { cn } from "nazare-ui/utils";

import type { ChannelType } from "../../../notifications-admin-api";
import { visibleChannels } from "../../../notification-channel-visibility";
import { PlatformModal } from "../../../platform-modal";
import { CHANNEL_ICON, CHANNEL_LABEL, NOTIFICATION_TYPE_GROUPS } from "./notification-catalogue";
import styles from "./bulk-edit-modal.module.css";

export type BulkEditPair = { type: string; channel: ChannelType };

export type BulkEditModalProps = {
  /** Whether the MCP channel is offered (the screen resolves the flag). */
  mcpEventsEnabled: boolean;
  /** How many users this'll apply to on `Apply` — purely informational
   * (the modal itself never touches `selectedUserIds`, the caller does —
   * see `AdminNotificationsPage`'s own `onApply`). */
  selectedCount: number;
  onCancel: () => void;
  /** Fires once, with every (type, channel) pair the admin picked plus the
   * single enabled/disabled value to apply to all of them — the caller
   * (`AdminNotificationsPage`) is what actually loops these through
   * `applyBulkChannelEdit`/`useBulkApplyPreference`, since that's also
   * where `selectedUserIds` lives. */
  onApply: (pairs: BulkEditPair[], enabled: boolean) => void;
};

function pairKey(type: string, channel: ChannelType): string {
  return `${type}:${channel}`;
}

/**
 * The secondary header's "Edit" button opens this — bulk-edit modal for the
 * `/notifications` admin matrix (`../index.tsx`'s `AdminNotificationsPage`).
 * Follows the exact `Dialog.Root/Portal/Overlay/Content` structure and
 * `.overlay`/`.content`/`.title`/`.actions`/`.cancelButton`/`.saveButton`
 * styling convention `../../avatar-upload-modal.tsx` already established as
 * this app's one real, working modal pattern — this is the second, not a
 * new one invented from scratch.
 *
 * Lets an admin pick one or more (notification type, channel) pairs — the
 * SAME `NOTIFICATION_TYPE_GROUPS` catalogue and per-type channel lists the
 * matrix itself renders, imported from `./notification-catalogue.ts` (shared
 * with `./index.tsx`, not duplicated) — plus a single Enabled/Disabled value, then applies that one
 * value to every picked pair for every currently-selected user in one go.
 * Picking zero pairs disables `Apply` — there's nothing to bulk-edit yet.
 */
export function BulkEditModal({ mcpEventsEnabled, selectedCount, onCancel, onApply }: BulkEditModalProps) {
  const [selectedPairs, setSelectedPairs] = useState<Set<string>>(new Set());
  const [enabledValue, setEnabledValue] = useState(true);

  const togglePair = (type: string, channel: ChannelType) => {
    const key = pairKey(type, channel);
    setSelectedPairs((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleApply = () => {
    if (selectedPairs.size === 0) return;
    const pairs: BulkEditPair[] = Array.from(selectedPairs, (key) => {
      // `type` itself can contain `.` (e.g. `scheduled_email.agenda-reminder`)
      // but never `:` — channel names are the fixed, colon-free
      // `ChannelType` union — so splitting on the LAST `:` unambiguously
      // recovers both halves regardless of how many dots the type has.
      const separator = key.lastIndexOf(":");
      return { type: key.slice(0, separator), channel: key.slice(separator + 1) as ChannelType };
    });
    onApply(pairs, enabledValue);
  };

  return (
    <PlatformModal
      title={`Bulk-edit ${selectedCount} user${selectedCount === 1 ? "" : "s"}`}
      description="Pick one or more notification/channel pairs below, choose a value, then apply it to every selected user at once."
      confirmLabel={`Apply to ${selectedCount} user${selectedCount === 1 ? "" : "s"}`}
      confirmDisabled={selectedPairs.size === 0}
      onClose={onCancel}
      onConfirm={handleApply}
    >
          <div className={styles.pairList}>
            {NOTIFICATION_TYPE_GROUPS.map((group) => (
              <div key={group.group} className={styles.pairGroup}>
                <span className={styles.pairGroupLabel}>{group.group}</span>
                {group.types.map((type) => (
                  <div key={type.type} className={styles.pairRow}>
                    <span className={styles.pairTypeLabel}>{type.label}</span>
                    <div className={styles.pairChipRow}>
                      {visibleChannels(type.channels, mcpEventsEnabled).map((channel) => {
                        const active = selectedPairs.has(pairKey(type.type, channel));
                        const Icon = CHANNEL_ICON[channel];
                        return (
                          <button
                            key={channel}
                            type="button"
                            className={cn(styles.pairChip, active && styles.pairChipActive)}
                            aria-pressed={active}
                            onClick={() => togglePair(type.type, channel)}
                          >
                            <Icon size={13} aria-hidden="true" />
                            {CHANNEL_LABEL[channel]}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className={styles.valueRow}>
            <span className={styles.valueLabel}>Set to</span>
            <div className={styles.valueToggle} role="radiogroup" aria-label="Value to apply">
              <button
                type="button"
                role="radio"
                aria-checked={enabledValue}
                className={cn(styles.valueOption, enabledValue && styles.valueOptionActive)}
                onClick={() => setEnabledValue(true)}
              >
                Enabled
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={!enabledValue}
                className={cn(styles.valueOption, !enabledValue && styles.valueOptionActive)}
                onClick={() => setEnabledValue(false)}
              >
                Disabled
              </button>
            </div>
          </div>

    </PlatformModal>
  );
}
